/**
 * Copyright (c) 2022, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import { useAuthContext } from "@asgardeo/auth-react";
import React, { FunctionComponent, ReactElement } from "react";
import { Divider, Header, Message } from "rsuite";
import {
    ERROR_DESCRIPTION_URL_SEARCH_PARAM_KEY,
    GENERIC_LOGOUT_ERROR_DESCRIPTION,
    USER_DENIED_ATTRIBUTE_CONSENT_ERROR_DESCRIPTION,
    USER_DENIED_LOGOUT_CONSENT_ERROR_DESCRIPTION
} from "../constants";
import { ErrorLayout } from "../layouts";
import { IdentifiableComponentInterface } from "../models";
import { canShowLogInButton, getSignInURL, handleSignOut } from "../utils";

/**
  * Error Page props interface.
  */
export type ErrorPagePropsInterface = IdentifiableComponentInterface;

/**
 * Error Page component.
 *
 * @param {ErrorPagePropsInterface} props - Props injected to the component. 
 * @returns {React.ReactElement}
 */
export const ErrorPage: FunctionComponent<ErrorPagePropsInterface> = (
    props: ErrorPagePropsInterface
): ReactElement => {

    const {
        [ "data-componentid" ]: componentId
    } = props;

    const { signOut } = useAuthContext();

    const errorDescriptionSearchParam: string = new URL(window.location.href)
        .searchParams.get(ERROR_DESCRIPTION_URL_SEARCH_PARAM_KEY) as string;

    const renderErrorContent = (): ReactElement => {
        if (errorDescriptionSearchParam === USER_DENIED_ATTRIBUTE_CONSENT_ERROR_DESCRIPTION) {
            return (
                <>
                    <Message showIcon type="error" header>
                        <Header as="h6">You have denied consent!</Header>
                    </Message>
                    <div className="content">
                        It looks like you have denied consent for the application to receive any of the user attributes.
                        If this was not your intention, please try the sign-in again.
                    </div>
                    {
                        canShowLogInButton() && (
                            <>
                                <Divider className="hidden" />
                                <a
                                    className="link"
                                    onClick={ () => window.open(getSignInURL(), "_self") }
                                >
                                    Sign In
                                </a>
                            </>
                        )
                    }
                </>
            );
        }

        if (errorDescriptionSearchParam === GENERIC_LOGOUT_ERROR_DESCRIPTION) {
            return (
                <>
                    <Message showIcon type="error" header>
                        <Header as="h6">Logout Out Error!</Header>
                    </Message>
                    <div className="content">
                        Oops! There seems to be an error in logging you out.
                        Please try the logout again.
                    </div>
                    <Divider className="hidden" />
                    <a
                        className="link"
                        onClick={ () => handleSignOut(() => {
                            signOut();
                        }) }
                    >
                        Logout
                    </a>
                </>
            );
        }

        if (errorDescriptionSearchParam === USER_DENIED_LOGOUT_CONSENT_ERROR_DESCRIPTION) {
            return (
                <>
                    <Message showIcon type="error" header>
                        <Header as="h6">You have denied consent to logout!</Header>
                    </Message>
                    <div className="content">
                        It looks like you have denied consent for logging out the application.
                        If this was not your intention, please try the logout again.
                    </div>
                    <Divider className="hidden" />
                    <a
                        className="link"
                        onClick={ () => handleSignOut(() => {
                            signOut();
                        }) }
                    >
                        Logout
                    </a>
                </>
            );
        }

        return (
            <>
                <Message showIcon type="error" header>
                    <Header as="h6">Error!</Header>
                </Message>
                <div className="content">
                    Something has gone wrong. Please retry the login again.
                </div>
                {
                    canShowLogInButton() && (
                        <>
                            <Divider className="hidden" />
                            <a
                                className="link"
                                onClick={ () => window.open(getSignInURL(), "_self") }
                            >
                                Sign In
                            </a>
                        </>
                    )
                }
            </>
        );
    };

    return (
        <ErrorLayout data-componentid={ componentId }>
            <div className="error-message-panel">
                { renderErrorContent() }
            </div>
        </ErrorLayout>
    );
};
